import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { ContractsService } from '../../../services/contracts.service';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import {
  ContractDocumentsResponse,
  ContractDocument,
  ContractFreezeResponse,
  ContractUnfreezeResponse,
  ContractActionLog
} from '../../../models/Contract';
import {
  statusAllowsContractFreeze,
  statusAllowsContractUnfreeze,
  statusIsContractFrozen
} from '../../../utils/contract-freeze.utils';
import { PaymentService } from '../../../services/payment.service';
import { AsaasPaymentResponse } from '../../../models/Payment';
import {
  ErpContractCancelAudit,
  normalizeContractCancellationHistoryFromApi,
  parseErpContractCancelFromPlaceholdersJson,
  parseErpContractCancelHistoryFromPlaceholdersJson
} from '../../../constants/contract-cancel-reasons';

/** Linha em um bloco de detalhes estruturados (detalhesJson). */
interface ContractActionDetailRow {
  label: string;
  value: string;
  isLink?: boolean;
}

/** Subseção (campos simples no topo ou objeto aninhado como card). */
interface ContractActionDetailSection {
  title: string | null;
  rows: ContractActionDetailRow[];
}

@Component({
  selector: 'app-contract-view',
  templateUrl: './contract-view.component.html',
  styleUrls: ['./contract-view.component.css']
})
export class ContractViewComponent implements OnInit, AfterViewInit {
  @ViewChild('signatureCanvas', { static: false }) signatureCanvasRef!: ElementRef<HTMLCanvasElement>;
  
  contractId!: number;
  contractData: any = null;
  isLoading = false;
  isLoadingDocument = false;
  processingStatus = '';
  
  contract: any = null;
  customer: any = null;
  vehicleDisplay: any = null;
  product: any = null;
  driver: any = null;
  documents: any[] = [];
  summary: any = null;
  
  contractDocuments: ContractDocumentsResponse | null = null;
  contractDocumentsList: ContractDocument[] = [];
  documentsSummary: any = null;
  documentsResumo: any = null;
  
  signedDocuments: ContractDocument[] = [];
  lastPendingDocument: ContractDocument | null = null;
  
  signatureCanvas: HTMLCanvasElement | null = null;
  signatureDataUrl: string | null = null;
  isDrawing = false;
  showSignatureModal = false;
  
  showDocumentViewer = false;
  selectedDocumentUrl: string | null = null;

  /** Menu ⋮ no cabeçalho */
  showContractHeaderMenu = false;
  showFreezeModal = false;

  // Asaas – cobranças vinculadas ao cliente deste contrato
  asaasCustomerId: string | null = null;
  asaasCharges: AsaasPaymentResponse[] = [];
  isLoadingAsaasCharges = false;
  asaasChargesError: string | null = null;

  /** Último cancelamento exibido em destaque. */
  erpContractCancel: ErpContractCancelAudit | null = null;
  /** Lista completa (`cancellationHistory` / `_erpContractCancelHistory`). */
  cancellationHistoryView: ErpContractCancelAudit[] = [];

  /** Histórico de ações (GET /view/contratos/{id}/acoes). */
  contractActionsLog: ContractActionLog[] = [];
  contractActionsTotal = 0;
  isLoadingContractActions = false;
  contractActionsError: string | null = null;
  private expandedContractActionIndexes = new Set<number>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private sanitizer: DomSanitizer,
    private paymentService: PaymentService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractComplete();
        this.loadContractDocuments();
        void this.loadContractActions();
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initializeSignatureCanvas(), 100);
  }

  async loadContractActions(): Promise<void> {
    if (!this.contractId) {
      return;
    }
    this.isLoadingContractActions = true;
    this.contractActionsError = null;
    try {
      const res = await firstValueFrom(this.contractsService.getContractActions(this.contractId));
      this.contractActionsTotal = res.quantidadeRegistros ?? res.acoes?.length ?? 0;
      const sorted = [...(res.acoes || [])].sort((a, b) => {
        const ta = new Date(a.ocorridoEm || a.criadoEm || 0).getTime();
        const tb = new Date(b.ocorridoEm || b.criadoEm || 0).getTime();
        return tb - ta;
      });
      this.contractActionsLog = sorted;
      this.expandedContractActionIndexes.clear();
    } catch (error: any) {
      this.contractActionsLog = [];
      this.contractActionsTotal = 0;
      this.contractActionsError =
        error?.error?.message || error?.message || 'Não foi possível carregar o histórico de ações.';
    } finally {
      this.isLoadingContractActions = false;
    }
  }

  trackByActionIndex(_index: number, _item: ContractActionLog): number {
    return _index;
  }

  toggleContractActionDetails(index: number): void {
    if (this.expandedContractActionIndexes.has(index)) {
      this.expandedContractActionIndexes.delete(index);
    } else {
      this.expandedContractActionIndexes.add(index);
    }
  }

  isContractActionExpanded(index: number): boolean {
    return this.expandedContractActionIndexes.has(index);
  }

  getContractActionWhen(action: ContractActionLog): string {
    const iso = action.ocorridoEm || action.criadoEm;
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  getContractActionExecutorLabel(action: ContractActionLog): string {
    const nome =
      action.nomeExibicaoExecutor?.trim() ||
      action.nomeColaboradorExecutor?.trim() ||
      action.emailUsuarioExecutor?.trim() ||
      action.criadoPor?.trim();
    if (!nome) return '—';
    if (nome.toLowerCase() === 'anonymous') return 'Anônimo / não identificado';
    return nome;
  }

  getContractActionExecutorExtra(action: ContractActionLog): string | null {
    const email = action.emailUsuarioExecutor?.trim();
    const nome = action.nomeExibicaoExecutor?.trim();
    if (email && nome && email.toLowerCase() !== nome.toLowerCase()) return email;
    return null;
  }

  /** Renovação com o mesmo produto: API pode mandar Contract.Renewed + descrição de migração; distinguimos por IDs no detalhesJson. */
  isContractRenewalSameProduct(action: ContractActionLog): boolean {
    const t = (action.tipoAcao || '').toLowerCase();
    if (!t.includes('renewed')) {
      return false;
    }
    const obj = this.parseContractActionDetailsObject(action.detalhesJson);
    if (!obj) {
      return false;
    }
    const cur = this.coerceContractDetailNumber(obj['productId'] ?? obj['ProductId']);
    const prev = this.coerceContractDetailNumber(
      obj['productIdAnterior'] ?? obj['ProductIdAnterior']
    );
    if (cur !== null && prev !== null) {
      return cur === prev;
    }
    const mig = obj['migracaoDePlano'] ?? obj['MigracaoDePlano'];
    if (cur !== null && prev === null && mig === false) {
      return true;
    }
    return false;
  }

  getContractActionDisplayTitle(action: ContractActionLog): string {
    const fallback = action.tipoAcaoDescricao || action.tipoAcao || 'Ação';
    if (!this.isContractRenewalSameProduct(action)) {
      return fallback;
    }
    return 'Renovação de contrato';
  }

  getContractActionDisplayResumo(action: ContractActionLog): string | null {
    const raw = action.resumo?.trim();
    if (!raw) {
      return null;
    }
    if (!this.isContractRenewalSameProduct(action)) {
      return action.resumo!;
    }
    return raw.replace(/\bMigração de plano\b/gi, 'Renovação');
  }

  private coerceContractDetailNumber(v: unknown): number | null {
    if (v === null || v === undefined || v === '') {
      return null;
    }
    const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  /**
   * JSON formatado para fallback quando o parse estruturado falhar.
   * Tenta corrigir escapes inválidos comuns da API (ex.: `\í` em vez de `í`).
   */
  formatContractActionDetailsJson(action: ContractActionLog): string {
    const raw = action.detalhesJson?.trim();
    if (!raw) return '';
    const parsed = this.parseContractActionDetailsObject(raw);
    if (parsed) {
      try {
        let cleaned = this.stripProductIdsFromDetailTree(parsed) as Record<string, unknown>;
        const isRenewedJson = (action.tipoAcao || '').toLowerCase().includes('renewed');
        if (isRenewedJson && this.looksLikeRenewalAuditPayload(cleaned)) {
          cleaned = this.mergeRenewalDetailsWithContractSnapshot(cleaned);
        }
        let ordered = this.maybeReorderRenewalDetailJson(action, cleaned);
        if (isRenewedJson && this.looksLikeRenewalAuditPayload(ordered)) {
          ordered = this.omitShallowDetailKeys(ordered, [
            'tipoOperacaoRenovacaoDescricao',
            'TipoOperacaoRenovacaoDescricao'
          ]);
        }
        return JSON.stringify(ordered, null, 2);
      } catch {
        /* segue para texto cru */
      }
    }
    const relaxed = raw.replace(/\\([^"\\/bfnrtu])/g, '$1');
    try {
      return JSON.stringify(JSON.parse(relaxed), null, 2);
    } catch {
      return relaxed;
    }
  }

  /** Retorna seções amigáveis para *ngFor ou null se não for objeto JSON válido. */
  getContractActionDetailSections(action: ContractActionLog): ContractActionDetailSection[] | null {
    const obj = this.parseContractActionDetailsObject(action.detalhesJson);
    if (!obj || Object.keys(obj).length === 0) return null;
    const cleaned = this.stripProductIdsFromDetailTree(obj) as Record<string, unknown>;
    const isRenewed = (action.tipoAcao || '').toLowerCase().includes('renewed');
    if (isRenewed && this.looksLikeRenewalAuditPayload(cleaned)) {
      const merged = this.mergeRenewalDetailsWithContractSnapshot(cleaned);
      const renewalSections = this.buildRenewalMigrationDetailSections(merged);
      if (renewalSections.some(s => s.rows.length > 0)) {
        return renewalSections;
      }
    }
    const built = this.buildContractActionDetailSections(cleaned);
    return built.some(s => s.rows.length > 0) ? built : null;
  }

  private parseContractActionDetailsObject(raw: string | null | undefined): Record<string, unknown> | null {
    const s = raw?.trim();
    if (!s) return null;
    const candidates = [s, s.replace(/\\([^"\\/bfnrtu])/g, '$1')];
    for (const t of candidates) {
      try {
        const parsed = JSON.parse(t) as unknown;
        if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
          return parsed as Record<string, unknown>;
        }
      } catch {
        continue;
      }
    }
    return null;
  }

  /** Remove IDs de produto da árvore exibida nos detalhes (mantém nomes como productNameNovo). */
  private stripProductIdsFromDetailTree(value: unknown): unknown {
    if (value === null || typeof value !== 'object') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map(item => this.stripProductIdsFromDetailTree(item));
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (this.isHiddenContractActionProductIdKey(k)) {
        continue;
      }
      out[k] = this.stripProductIdsFromDetailTree(v);
    }
    return out;
  }

  /** Chaves numéricas internas de produto que não devem aparecer para o usuário. */
  private isHiddenContractActionProductIdKey(key: string): boolean {
    const n = key.trim().replace(/_/g, '').toLowerCase();
    return n === 'productid' || n === 'productidanterior';
  }

  private looksLikeRenewalAuditPayload(obj: Record<string, unknown>): boolean {
    return (
      this.pickDetailNestedObject(obj, ['dadosNovos', 'DadosNovos']) != null ||
      this.pickDetailNestedObject(obj, ['dadosAntigos', 'DadosAntigos']) != null ||
      this.hasAnyOwnDetailKey(obj, ['productNameNovo', 'ProductNameNovo']) ||
      this.hasAnyOwnDetailKey(obj, ['productNameAnterior', 'ProductNameAnterior'])
    );
  }

  private hasAnyOwnDetailKey(obj: Record<string, unknown>, keys: string[]): boolean {
    return keys.some(
      k =>
        Object.prototype.hasOwnProperty.call(obj, k) &&
        obj[k] !== undefined &&
        obj[k] !== null
    );
  }

  private pickDetailNestedObject(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> | null {
    for (const k of keys) {
      const v = obj[k];
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        return v as Record<string, unknown>;
      }
    }
    return null;
  }

  /**
   * Se o histórico não trouxer `dadosNovos`, monta o mesmo formato de `dadosAntigos`
   * a partir do contrato já carregado nesta tela (valor base, caução, ciclo, datas).
   */
  private mergeRenewalDetailsWithContractSnapshot(cleaned: Record<string, unknown>): Record<string, unknown> {
    const existing = this.pickDetailNestedObject(cleaned, ['dadosNovos', 'DadosNovos']);
    if (existing && Object.keys(existing).length > 0) {
      return cleaned;
    }
    const synthetic = this.buildSyntheticDadosNovosFromContract();
    if (!synthetic || Object.keys(synthetic).length === 0) {
      return cleaned;
    }
    return { ...cleaned, dadosNovos: synthetic };
  }

  private buildSyntheticDadosNovosFromContract(): Record<string, unknown> | null {
    const c = this.contract as Record<string, unknown> | undefined;
    if (!c || typeof c !== 'object') {
      return null;
    }

    const out: Record<string, unknown> = {};

    const startFmt = this.formatAuditStyleContractDate(c['startDate']);
    const endFmt = this.formatAuditStyleContractDate(c['endDate']);
    if (startFmt) {
      out['startDate'] = startFmt;
    }
    if (endFmt) {
      out['endDate'] = endFmt;
    }

    const baseRaw = c['baseAmount'];
    if (baseRaw !== undefined && baseRaw !== null && baseRaw !== '') {
      const n = typeof baseRaw === 'number' ? baseRaw : Number(String(baseRaw).replace(',', '.'));
      if (!Number.isNaN(n)) {
        out['baseAmount'] = n;
      }
    }

    const depRaw = c['depositAmount'];
    if (depRaw !== undefined && depRaw !== null && depRaw !== '') {
      const n = typeof depRaw === 'number' ? depRaw : Number(String(depRaw).replace(',', '.'));
      if (!Number.isNaN(n)) {
        out['depositAmount'] = n;
      }
    }

    const cycle = c['billingCycle'];
    if (cycle !== undefined && cycle !== null && String(cycle).trim() !== '') {
      out['billingCycle'] = String(cycle);
    }

    const st = c['status'];
    if (st !== undefined && st !== null && String(st).trim() !== '') {
      out['status'] = this.getStatusLabel(String(st));
    }

    return Object.keys(out).length > 0 ? out : null;
  }

  private formatAuditStyleContractDate(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const s = String(value).trim();
    if (!s) {
      return null;
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('pt-BR');
    }
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) {
      return s;
    }
    return s;
  }

  private omitShallowDetailKeys(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> {
    const out = { ...obj };
    for (const k of keys) {
      delete out[k];
    }
    return out;
  }

  /** Oculta linha Entidade quando é só documento de contrato (ruído técnico). */
  shouldShowContractActionEntityRow(action: ContractActionLog): boolean {
    const tipo = (action.tipoEntidadeRelacionada || '').replace(/\s+/g, '').toLowerCase();
    const id = action.idEntidadeRelacionada;
    if (!tipo && id == null) {
      return false;
    }
    if (tipo === 'contractdocument') {
      return false;
    }
    return !!(tipo || id != null);
  }

  /** Destaca plano novo primeiro; encaixa `dadosNovos` da API quando existir. */
  private buildRenewalMigrationDetailSections(obj: Record<string, unknown>): ContractActionDetailSection[] {
    const consumed = new Set<string>();
    for (const k of ['tipoOperacaoRenovacaoDescricao', 'TipoOperacaoRenovacaoDescricao']) {
      if (Object.prototype.hasOwnProperty.call(obj, k)) {
        consumed.add(k);
      }
    }
    const sections: ContractActionDetailSection[] = [];

    const novoRows: ContractActionDetailRow[] = [];
    const dadosNovos = this.pickDetailNestedObject(obj, ['dadosNovos', 'DadosNovos']);
    if (dadosNovos) {
      novoRows.push(...this.recordToContractDetailRows(dadosNovos));
      consumed.add('dadosNovos');
      consumed.add('DadosNovos');
    }

    this.pushRenewalScalarRowVariants(obj, consumed, novoRows, ['productNameNovo', 'ProductNameNovo']);
    this.pushRenewalScalarRowVariants(obj, consumed, novoRows, ['versaoDocumento', 'VersaoDocumento']);
    this.pushRenewalScalarRowVariants(obj, consumed, novoRows, ['tipoOperacaoRenovacao', 'TipoOperacaoRenovacao']);
    this.pushRenewalScalarRowVariants(obj, consumed, novoRows, ['migracaoDePlano', 'MigracaoDePlano']);

    if (!novoRows.length) {
      novoRows.push({
        label: 'Observação',
        value:
          'Este registro não traz valores do plano novo e o contrato ainda não está disponível para completar aqui. Veja Produto / Plano na página.'
      });
    }

    sections.push({ title: 'Plano novo', rows: novoRows });

    const antigoRows: ContractActionDetailRow[] = [];
    this.pushRenewalScalarRowVariants(obj, consumed, antigoRows, [
      'productNameAnterior',
      'ProductNameAnterior'
    ]);
    const dadosAntigos = this.pickDetailNestedObject(obj, ['dadosAntigos', 'DadosAntigos']);
    if (dadosAntigos) {
      antigoRows.push(...this.recordToContractDetailRows(dadosAntigos));
      consumed.add('dadosAntigos');
      consumed.add('DadosAntigos');
    }
    if (antigoRows.length) {
      sections.push({ title: 'Plano anterior', rows: antigoRows });
    }

    const remainder: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (!consumed.has(k)) {
        remainder[k] = v;
      }
    }
    if (Object.keys(remainder).length > 0) {
      sections.push(...this.buildContractActionDetailSections(remainder));
    }

    return sections;
  }

  private pushRenewalScalarRowVariants(
    obj: Record<string, unknown>,
    consumed: Set<string>,
    rows: ContractActionDetailRow[],
    variantKeys: string[]
  ): void {
    for (const key of variantKeys) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      const val = obj[key];
      if (val !== null && typeof val === 'object' && !Array.isArray(val)) continue;
      variantKeys.forEach(k => consumed.add(k));
      const f = this.formatContractDetailValue(val, key);
      rows.push({
        label: this.contractActionDetailKeyLabel(key),
        value: f.text,
        isLink: f.isLink
      });
      return;
    }
  }

  /** JSON técnico: plano novo antes do anterior (mesma ordem das seções). */
  private maybeReorderRenewalDetailJson(
    action: ContractActionLog,
    obj: Record<string, unknown>
  ): Record<string, unknown> {
    const isRenewed = (action.tipoAcao || '').toLowerCase().includes('renewed');
    if (!isRenewed || !this.looksLikeRenewalAuditPayload(obj)) {
      return obj;
    }
    const out: Record<string, unknown> = {};
    const consumed = new Set<string>();
    const takeKeys = (keys: string[]) => {
      for (const k of keys) {
        if (Object.prototype.hasOwnProperty.call(obj, k)) {
          out[k] = obj[k];
          consumed.add(k);
        }
      }
    };
    takeKeys(['dadosNovos', 'DadosNovos']);
    takeKeys([
      'productNameNovo',
      'ProductNameNovo',
      'versaoDocumento',
      'VersaoDocumento',
      'tipoOperacaoRenovacao',
      'TipoOperacaoRenovacao',
      'migracaoDePlano',
      'MigracaoDePlano'
    ]);
    takeKeys(['productNameAnterior', 'ProductNameAnterior']);
    takeKeys(['dadosAntigos', 'DadosAntigos']);
    const omitFromRenewalJson = new Set(['tipoOperacaoRenovacaoDescricao', 'TipoOperacaoRenovacaoDescricao']);
    for (const [k, v] of Object.entries(obj)) {
      if (!consumed.has(k) && !omitFromRenewalJson.has(k)) {
        out[k] = v;
      }
    }
    return out;
  }

  private buildContractActionDetailSections(obj: Record<string, unknown>): ContractActionDetailSection[] {
    const scalarRows: ContractActionDetailRow[] = [];
    const objectSections: ContractActionDetailSection[] = [];

    for (const [key, val] of Object.entries(obj)) {
      if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
        objectSections.push({
          title: this.contractActionDetailKeyLabel(key),
          rows: this.recordToContractDetailRows(val as Record<string, unknown>)
        });
      } else if (Array.isArray(val)) {
        scalarRows.push({
          label: this.contractActionDetailKeyLabel(key),
          value: this.formatContractDetailArray(val, key)
        });
      } else {
        const f = this.formatContractDetailValue(val, key);
        scalarRows.push({
          label: this.contractActionDetailKeyLabel(key),
          value: f.text,
          isLink: f.isLink
        });
      }
    }

    const out: ContractActionDetailSection[] = [];
    if (scalarRows.length) {
      out.push({ title: null, rows: scalarRows });
    }
    out.push(...objectSections);
    return out;
  }

  private recordToContractDetailRows(rec: Record<string, unknown>): ContractActionDetailRow[] {
    return Object.entries(rec).map(([k, v]) => {
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        try {
          const nested = this.recordToContractDetailRows(v as Record<string, unknown>)
            .map(r => `${r.label}: ${r.value}`)
            .join(' · ');
          return {
            label: this.contractActionDetailKeyLabel(k),
            value: nested || JSON.stringify(v)
          };
        } catch {
          return {
            label: this.contractActionDetailKeyLabel(k),
            value: JSON.stringify(v)
          };
        }
      }
      if (Array.isArray(v)) {
        return {
          label: this.contractActionDetailKeyLabel(k),
          value: this.formatContractDetailArray(v, k)
        };
      }
      const f = this.formatContractDetailValue(v, k);
      return {
        label: this.contractActionDetailKeyLabel(k),
        value: f.text,
        isLink: f.isLink
      };
    });
  }

  private formatContractDetailArray(arr: unknown[], key: string): string {
    if (!arr.length) return '—';
    return arr
      .map((item, i) => {
        if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
          const inner = this.recordToContractDetailRows(item as Record<string, unknown>)
            .map(r => `${r.label}: ${r.value}`)
            .join(', ');
          return `${i + 1}) ${inner}`;
        }
        const f = this.formatContractDetailValue(item, `${key}[${i}]`);
        return `${i + 1}) ${f.text}`;
      })
      .join('\n');
  }

  private formatContractDetailValue(
    val: unknown,
    key?: string
  ): { text: string; isLink?: boolean } {
    if (val === null || val === undefined) return { text: '—' };
    if (typeof val === 'boolean') return { text: val ? 'Sim' : 'Não' };
    if (typeof val === 'number') {
      if (key && /amount$/i.test(key)) {
        return {
          text: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val)
        };
      }
      return {
        text: Number.isInteger(val) ? String(val) : val.toLocaleString('pt-BR', { maximumFractionDigits: 6 })
      };
    }
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (!trimmed) return { text: '—' };
      if (key && /plate$/i.test(key)) {
        return { text: trimmed.toUpperCase() };
      }
      if (/^https?:\/\//i.test(trimmed)) {
        return { text: trimmed, isLink: true };
      }
      const isoDate = this.tryFormatContractDetailIso(trimmed, key);
      if (isoDate) return { text: isoDate };
      const num = Number(trimmed.replace(',', '.'));
      if (trimmed.match(/^-?\d+([.,]\d+)?$/) && !Number.isNaN(num)) {
        return { text: num.toLocaleString('pt-BR') };
      }
      return { text: val };
    }
    try {
      return { text: JSON.stringify(val, null, 2) };
    } catch {
      return { text: String(val) };
    }
  }

  private tryFormatContractDetailIso(s: string, key?: string): string | null {
    if (!/^\d{4}-\d{2}-\d{2}T/.test(s)) return null;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    const opts: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    };
    const useUtc = !!(key && /utc$/i.test(key));
    if (useUtc) {
      return (
        d.toLocaleString('pt-BR', { ...opts, timeZone: 'UTC' }) +
        ' (UTC)'
      );
    }
    return d.toLocaleString('pt-BR', opts);
  }

  private contractActionDetailKeyLabel(key: string): string {
    const raw = key.trim();
    const camelKey =
      /^[A-Z]/.test(raw) && raw.length > 1 ? raw.charAt(0).toLowerCase() + raw.slice(1) : raw;

    const labels: Record<string, string> = {
      // Criação do contrato (Contract.Created) e metadados semelhantes
      productName: 'Produto',
      productCode: 'Código do produto',
      customerName: 'Cliente',
      customerId: 'ID do cliente',
      customerDocument: 'Documento do cliente',
      vehiclePlate: 'Placa do veículo',
      vehicleModel: 'Modelo do veículo',
      vehicleId: 'ID do veículo',
      documentTemplate: 'Modelo de documento',
      documentTemplateName: 'Nome do modelo de documento',
      documentTemplateId: 'ID do modelo de documento',
      templateName: 'Nome do template',
      templateVersion: 'Versão do template',
      templateVersionId: 'ID da versão do template',
      contractNumber: 'Número do contrato',
      contractId: 'ID do contrato',
      draftId: 'ID do rascunho',
      driverName: 'Motorista',
      driverId: 'ID do motorista',
      // Congelamento / ciclo de vida
      motivo: 'Motivo',
      freezeStartUtc: 'Início do congelamento',
      freezeEndUtc: 'Fim do congelamento',
      storageKey: 'Chave no armazenamento',
      blobUrl: 'URL do arquivo',
      versaoDocumento: 'Versão do documento',
      dadosNovos: 'Dados do plano novo',
      productNameNovo: 'Produto (novo plano)',
      productNameAnterior: 'Produto (plano anterior)',
      tipoOperacaoRenovacao: 'Tipo de operação',
      migracaoDePlano: 'Migração de plano',
      veiculoAntigo: 'Veículo anterior',
      veiculoNovo: 'Veículo novo',
      dadosAntigos: 'Dados anteriores',
      plate: 'Placa',
      model: 'Modelo',
      year: 'Ano',
      color: 'Cor',
      brand: 'Marca',
      renavam: 'RENAVAM',
      chassis: 'Chassi',
      startDate: 'Data de início',
      endDate: 'Data de término',
      baseAmount: 'Valor base',
      depositAmount: 'Caução',
      billingCycle: 'Ciclo de cobrança',
      status: 'Status',
      currentKm: 'Quilometragem atual',
      inspectionNotes: 'Observações de vistoria',
      cpf: 'CPF',
      cnpj: 'CNPJ',
      email: 'E-mail',
      phone: 'Telefone',
      reason: 'Motivo',
      notes: 'Observações',
      comment: 'Comentário',
      observacoes: 'Observações',
      observacoesLegadoNaoJson: 'Observações (texto legado)',
      _erpRenewOrMigrate: 'Tipo de operação',
      _erpRenewOrMigrateAtLocal: 'Registrado em (local)'
    };
    return labels[raw] ?? labels[camelKey] ?? this.humanizeContractDetailKey(raw);
  }

  private humanizeContractDetailKey(key: string): string {
    const spaced = key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .trim();
    if (!spaced) return key;
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  hasContractActionExpandableContent(action: ContractActionLog): boolean {
    return !!action.detalhesJson?.trim() || !!action.enderecoIp?.trim();
  }

  getContractActionCategoryClass(categoria: string | null | undefined): string {
    const u = (categoria || '').toLowerCase();
    if (u === 'lifecycle') return 'cv-action-pill-lifecycle';
    if (u === 'fleet') return 'cv-action-pill-fleet';
    return 'cv-action-pill-default';
  }

  /** Rótulo PT-BR para a pill de categoria do histórico de ações. */
  getContractActionCategoryLabel(categoria: string | null | undefined): string {
    const c = (categoria || '').trim();
    if (!c) return 'Geral';
    const u = c.toLowerCase();
    if (u === 'lifecycle') return 'Ciclo de vida';
    return c;
  }

  getContractActionIcon(tipoAcao: string | null | undefined): string {
    const t = (tipoAcao || '').toLowerCase();
    if (t.includes('.created') || t.endsWith('created')) return 'fa-file-circle-plus';
    if (t.includes('frozen') && !t.includes('unfrozen')) return 'fa-snowflake';
    if (t.includes('unfrozen')) return 'fa-sun';
    if (t.includes('signed')) return 'fa-file-signature';
    if (t.includes('renewed')) return 'fa-sync-alt';
    if (t.includes('vehicle')) return 'fa-motorcycle';
    if (t.includes('cancel')) return 'fa-ban';
    return 'fa-history';
  }

  async loadContractComplete(): Promise<void> {
    try {
      this.isLoading = true;
      const contractData = await firstValueFrom(
        this.contractsService.getContractComplete(this.contractId)
      );
      
      this.contractData = contractData;
      this.contract = contractData.contract;
      this.customer = contractData.customer;
      this.vehicleDisplay = contractData.vehicleSnapshot || contractData.vehicle;
      this.product = contractData.product;
      this.driver = contractData.driver;
      this.documents = contractData.documents || [];
      this.summary = this.calculateSummary();
      this.refreshErpContractCancelFromContract();

    } catch (error: any) {
      this.errorService.showErrorMessage('Erro ao carregar contrato: ' + error.message);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadAsaasChargesForCurrentCustomer(): Promise<void> {
    // Cliente ou motorista precisam ter CPF/CNPJ para localizar o customer no Asaas
    const doc =
      this.customer?.documentNumber ||
      (this.customer as any)?.cpfCnpj ||
      this.driver?.cpfCnpj;
    if (!doc) {
      this.asaasCharges = [];
      this.asaasCustomerId = null;
      return;
    }

    const digits = String(doc).replace(/\D/g, '');
    if (!digits || digits.length < 11) {
      // Documento inválido para consulta Asaas
      this.asaasCharges = [];
      this.asaasCustomerId = null;
      return;
    }

    try {
      this.isLoadingAsaasCharges = true;
      this.asaasChargesError = null;
      this.asaasCharges = [];
      this.asaasCustomerId = null;

      // 1) Localizar customer no Asaas pelo CPF/CNPJ
      const filters: { cpfCnpj?: string } = { cpfCnpj: digits };
      const customersRes = await firstValueFrom(
        this.paymentService.listAsaasCustomers(filters)
      );

      const firstCustomer = customersRes?.data && customersRes.data.length > 0
        ? customersRes.data[0]
        : null;

      if (!firstCustomer?.id) {
        // Sem cliente no Asaas → sem cobranças para exibir
        return;
      }

      this.asaasCustomerId = firstCustomer.id;

      // 2) Buscar cobranças desse customer
      const chargesRes = await firstValueFrom(
        this.paymentService.listAsaasCharges({ customer: this.asaasCustomerId || undefined, limit: 50 })
      );

      const data = chargesRes?.data || chargesRes?.Data || [];
      this.asaasCharges = (data as AsaasPaymentResponse[]).sort((a, b) => {
        const d1 = (a.dueDate || '').toString();
        const d2 = (b.dueDate || '').toString();
        return d1.localeCompare(d2);
      });
    } catch (error: any) {
      this.asaasChargesError = error?.message || 'Erro ao carregar cobranças Asaas.';
      this.asaasCharges = [];
      this.asaasCustomerId = null;
    } finally {
      this.isLoadingAsaasCharges = false;
    }
  }

  formatAsaasStatus(status: string | undefined | null): string {
    if (!status) return 'N/A';
    const s = status.toUpperCase();
    if (s === 'PENDING') return 'Pendente';
    if (s === 'RECEIVED') return 'Recebido';
    if (s === 'CONFIRMED') return 'Confirmado';
    if (s === 'CANCELLED') return 'Cancelado';
    if (s === 'OVERDUE') return 'Vencido';
    if (s === 'REFUNDED') return 'Estornado';
    return status;
  }

  async loadContractDocuments(): Promise<void> {
    try {
      const documentsData = await firstValueFrom(
        this.contractsService.getContractDocuments(this.contractId)
      );
      
      this.contractDocuments = documentsData;
      this.contractDocumentsList = documentsData.documentos || [];
      this.documentsSummary = documentsData.contrato || null;
      this.documentsResumo = documentsData.resumo || null;
      this.filterDocumentsBySecurity();
    } catch (error: any) {
      this.errorService.showErrorMessage('Erro ao carregar versões dos documentos: ' + error.message);
    }
  }

  private filterDocumentsBySecurity(): void {
    if (!this.contractDocumentsList || this.contractDocumentsList.length === 0) {
      this.signedDocuments = [];
      this.lastPendingDocument = null;
      return;
    }

    const sortedDocs = [...this.contractDocumentsList].sort((a, b) => b.versao - a.versao);
    const signed: ContractDocument[] = [];
    const pending: ContractDocument[] = [];
    
    sortedDocs.forEach(doc => {
      const statusUpper = (doc.signatureStatus || '').toString().toUpperCase();
      const isSigned = doc.isAssinado === true || 
                       ['SIGNED', 'ASSINADO', 'CONCLUÍDO', 'CONCLUIDO'].includes(statusUpper);
      const isPending = doc.isPendente === true || 
                        ['PENDING', 'PENDENTE', 'AGUARDANDO'].includes(statusUpper);
      
      if (isSigned) {
        signed.push(doc);
      } else if (isPending) {
        pending.push(doc);
      } else if (doc.storageUrl && doc.providerDocumentId) {
        signed.push(doc);
      } else {
        pending.push(doc);
      }
    });
    
    this.lastPendingDocument = pending.length > 0 ? pending[0] : null;
    
    if (this.lastPendingDocument && signed.length === 0) {
      this.lastPendingDocument.podeAssinar = true;
      this.lastPendingDocument.isPendente = true;
    }
    
    this.signedDocuments = signed.sort((a, b) => b.versao - a.versao);
  }

  initializeSignatureCanvas(): void {
    if (!this.signatureCanvasRef) return;
    
    this.signatureCanvas = this.signatureCanvasRef.nativeElement;
    const ctx = this.signatureCanvas.getContext('2d');
    if (!ctx) return;
    
    this.signatureCanvas.width = 400;
    this.signatureCanvas.height = 150;
    
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, this.signatureCanvas.width, this.signatureCanvas.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    this.signatureCanvas.addEventListener('mousedown', e => this.startDrawing(e));
    this.signatureCanvas.addEventListener('mousemove', e => this.draw(e));
    this.signatureCanvas.addEventListener('mouseup', () => this.stopDrawing());
    this.signatureCanvas.addEventListener('mouseout', () => this.stopDrawing());
    this.signatureCanvas.addEventListener('touchstart', e => {
      e.preventDefault();
      this.startDrawing(e);
    });
    this.signatureCanvas.addEventListener('touchmove', e => {
      e.preventDefault();
      this.draw(e);
    });
    this.signatureCanvas.addEventListener('touchend', e => {
      e.preventDefault();
      this.stopDrawing();
    });
  }

  openSignatureModal(): void {
    this.showSignatureModal = true;
    this.signatureDataUrl = null;
    this.isDrawing = false;
    setTimeout(() => this.initializeSignatureCanvas(), 200);
  }

  closeSignatureModal(): void {
    this.showSignatureModal = false;
    this.signatureDataUrl = null;
  }

  startDrawing(event: MouseEvent | TouchEvent): void {
    if (!this.signatureCanvas) return;
    
    this.isDrawing = true;
    const ctx = this.signatureCanvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = this.getCanvasCoordinates(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  draw(event: MouseEvent | TouchEvent): void {
    if (!this.isDrawing || !this.signatureCanvas) return;
    
    const ctx = this.signatureCanvas.getContext('2d');
    if (!ctx) return;

    const { x, y } = this.getCanvasCoordinates(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  stopDrawing(): void {
    this.isDrawing = false;
  }

  private getCanvasCoordinates(event: MouseEvent | TouchEvent): { x: number; y: number } {
    if (!this.signatureCanvas) return { x: 0, y: 0 };

    const rect = this.signatureCanvas.getBoundingClientRect();
    const scaleX = this.signatureCanvas.width / rect.width;
    const scaleY = this.signatureCanvas.height / rect.height;
    
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }

  clearSignature(): void {
    if (!this.signatureCanvas) return;
    const ctx = this.signatureCanvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, this.signatureCanvas.width, this.signatureCanvas.height);
    }
  }

  async saveSignature(): Promise<void> {
    if (!this.signatureCanvas) {
      this.errorService.showErrorMessage('Canvas de assinatura não encontrado');
      return;
    }

    const ctx = this.signatureCanvas.getContext('2d');
    if (!ctx) {
      this.errorService.showErrorMessage('Não foi possível acessar o contexto do canvas');
      return;
    }
    
    const imageData = ctx.getImageData(0, 0, this.signatureCanvas.width, this.signatureCanvas.height);
    const hasDrawing = imageData.data.some(channel => channel !== 0);

    if (!hasDrawing) {
      this.errorService.showErrorMessage('Por favor, desenhe sua assinatura antes de salvar');
      return;
    }

    const dataUrl = this.signatureCanvas.toDataURL('image/png');
    const base64Signature = dataUrl.split(',')[1];
    this.signatureDataUrl = dataUrl;
    
    try {
      this.closeSignatureModal();
      this.isLoading = true;
      this.processingStatus = 'Assinando contrato...';
      
      await firstValueFrom(
        this.contractsService.signContract(this.contractId, { signature: base64Signature })
      );
      
      this.errorService.showInfoMessage('💚 Contrato assinado com sucesso!');
      await this.loadContractComplete();
      await this.loadContractDocuments();
    } catch (error: any) {
      this.errorService.showErrorMessage('Erro ao assinar contrato: ' + (error.message || 'Erro desconhecido'));
    } finally {
      this.isLoading = false;
      this.processingStatus = '';
    }
  }

  viewDocument(document: any): void {
    if (document.storageUrl) {
      this.selectedDocumentUrl = document.storageUrl;
      this.showDocumentViewer = true;
      this.isLoadingDocument = false;
    } else if (document.storageKey) {
      this.errorService.showErrorMessage('URL do documento não está disponível. O documento pode estar sendo processado.');
    } else {
      this.errorService.showErrorMessage('URL do documento não disponível. Entre em contato com o suporte se o problema persistir.');
    }
  }

  signDocument(document: ContractDocument): void {
    const hasNoSignedDocuments = this.signedDocuments.length === 0;
    const canSign = document.podeAssinar && document.isPendente;
    const isFirstContract = hasNoSignedDocuments && document.isPendente;
    
    if (!canSign && !isFirstContract) {
      this.errorService.showErrorMessage('Este documento não pode ser assinado no momento');
      return;
    }
    
    if (isFirstContract) {
      document.podeAssinar = true;
      document.isPendente = true;
    }
    
    this.openSignatureModal();
  }

  getOfficeViewerUrl(): SafeResourceUrl {
    if (!this.selectedDocumentUrl) {
      return this.sanitizer.bypassSecurityTrustResourceUrl('');
    }
    const encodedUrl = encodeURIComponent(this.selectedDocumentUrl);
    const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
  }

  closeDocumentViewer(): void {
    this.showDocumentViewer = false;
    this.selectedDocumentUrl = null;
  }

  formatPrice(value: number): string {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  formatDate(date: string | Date): string {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR');
  }

  formatTime(date: string | Date): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    dateObj.setHours(dateObj.getHours() - 3);
    return dateObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  formatFileSize(bytes: number): string {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  getSignatureStatusLabel(status: string): string {
    const statusMap: Record<string, string> = {
      'PENDING': 'Pendente',
      'SIGNED': 'Assinado',
      'REJECTED': 'Rejeitado'
    };
    return statusMap[status] || 'Desconhecido';
  }

  getStatusClass(status: string): string {
    const statusUpper = status?.toUpperCase();
    const classMap: Record<string, string> = {
      'ACTIVE': 'badge-success',
      'VIGENTE': 'badge-success',
      'INACTIVE': 'badge-secondary',
      'INATIVO': 'badge-secondary',
      'EXPIRED': 'badge-danger',
      'EXPIRADO': 'badge-danger',
      'PENDING': 'badge-warning',
      'PENDENTE': 'badge-warning',
      'DRAFT': 'badge-info',
      'RASCUNHO': 'badge-info'
    };
    return classMap[statusUpper!] || 'badge-secondary';
  }

  /** Modificador visual do status do contrato (usa com `class="cv-pill"`). */
  getCvContractStatusPillModifier(status: string | undefined): string {
    if (statusIsContractFrozen(status)) {
      return 'cv-pill-ice';
    }
    const u = (status || '').toUpperCase();
    if (['ACTIVE', 'VIGENTE'].includes(u)) return 'cv-pill-success';
    if (['PENDING', 'PENDENTE'].includes(u) || u.includes('AGUARDANDO')) return 'cv-pill-warn-soft';
    if (['EXPIRED', 'EXPIRADO', 'CANCELLED', 'CANCELADO'].includes(u)) return 'cv-pill-danger';
    if (['DRAFT', 'RASCUNHO'].includes(u)) return 'cv-pill-info';
    return 'cv-pill-neutral';
  }

  /** Modificador para pills de assinatura em documentos legados. */
  getCvSignaturePillModifier(status: string | undefined): string {
    const u = (status || '').toString().toUpperCase();
    if (u === 'SIGNED' || u === 'ASSINADO') return 'cv-pill-success';
    if (u === 'REJECTED' || u === 'REJEITADO') return 'cv-pill-danger';
    if (u === 'PENDING' || u === 'PENDENTE') return 'cv-pill-warn-soft';
    return 'cv-pill-neutral';
  }

  reloadContractDocuments(): void {
    void this.loadContractDocuments();
  }

  getStatusLabel(status: string): string {
    const statusUpper = status?.toUpperCase();
    const labelMap: Record<string, string> = {
      'ACTIVE': 'Vigente',
      'VIGENTE': 'Vigente',
      'INACTIVE': 'Inativo',
      'INATIVO': 'Inativo',
      'EXPIRED': 'Expirado',
      'EXPIRADO': 'Expirado',
      'PENDING': 'Pendente',
      'PENDENTE': 'Pendente',
      'DRAFT': 'Rascunho',
      'RASCUNHO': 'Rascunho'
    };
    return labelMap[statusUpper!] || status || 'Desconhecido';
  }

  calculateSummary(): any {
    const totalDocuments = this.documents.length;
    const pendingSignatures = this.documents.filter(doc => doc.signatureStatus === 'PENDING').length;
    const completedSignatures = this.documents.filter(doc => doc.signatureStatus === 'SIGNED').length;
    const totalFileSize = this.documents.reduce((sum, doc) => sum + (doc.fileSizeBytes || 0), 0);
    
    let contractDuration = 0;
    if (this.contract?.startDate && this.contract?.endDate) {
      const start = new Date(this.contract.startDate);
      const end = new Date(this.contract.endDate);
      contractDuration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    }
    
    let daysRemaining = 0;
    if (this.contract?.endDate) {
      const end = new Date(this.contract.endDate);
      const now = new Date();
      daysRemaining = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    }

    return {
      totalDocuments,
      pendingSignatures,
      completedSignatures,
      totalFileSize,
      contractDuration,
      daysRemaining
    };
  }

  downloadDocumentFile(doc: any): void {
    if (doc.storageUrl) {
      this.downloadFile(doc.storageUrl, doc.fileName || 'documento');
    } else {
      this.errorService.showErrorMessage('URL do documento não disponível');
    }
  }

  printDocument(doc: any): void {
    if (doc.storageUrl) {
      window.open(doc.storageUrl, '_blank');
    } else {
      this.errorService.showErrorMessage('URL do documento não disponível');
    }
  }

  downloadCurrentDocument(): void {
    if (this.selectedDocumentUrl) {
      this.downloadFile(this.selectedDocumentUrl, `contrato-${this.contractId}.docx`);
    } else {
      this.errorService.showErrorMessage('Nenhum documento selecionado');
    }
  }

  private downloadFile(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private refreshErpContractCancelFromContract(): void {
    const c = this.contract;
    this.erpContractCancel = null;
    this.cancellationHistoryView = [];
    if (!c) return;

    const apiHistory = normalizeContractCancellationHistoryFromApi(
      c.cancellationHistory ?? c['CancellationHistory']
    );
    if (apiHistory.length > 0) {
      this.cancellationHistoryView = [...apiHistory];
      this.erpContractCancel = apiHistory[apiHistory.length - 1];
      return;
    }

    const lastMotivo = c.lastCancellationMotivo ?? c['LastCancellationMotivo'];
    const lastAt = c.lastCancellationAtUtc ?? c['LastCancellationAtUtc'];
    if (typeof lastMotivo === 'string' && lastMotivo.trim()) {
      this.erpContractCancel = {
        motivo: lastMotivo.trim(),
        cancelledAtUtc: typeof lastAt === 'string' ? lastAt : ''
      };
      this.cancellationHistoryView = [this.erpContractCancel];
      return;
    }

    const json = c.placeholdersDataJson ?? c.PlaceholdersDataJson ?? null;
    const fromPh = parseErpContractCancelHistoryFromPlaceholdersJson(json);
    if (fromPh.length > 0) {
      this.cancellationHistoryView = [...fromPh];
      this.erpContractCancel = fromPh[fromPh.length - 1];
      return;
    }

    const single = parseErpContractCancelFromPlaceholdersJson(json);
    if (single) {
      this.erpContractCancel = single;
      this.cancellationHistoryView = [single];
    }
  }

  /** Contrato em estado cancelado (várias grafias possíveis no backend). */
  isContractCancelledView(): boolean {
    const s = String(this.contract?.status ?? '').toLowerCase();
    return s.includes('cancel');
  }

  /** Exibe data/hora de cancelamento gravada na auditoria (ISO UTC). */
  formatCancelledAtDisplay(iso: string): string {
    if (!iso?.trim()) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return (
      d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC'
      }) + ' UTC'
    );
  }

  navigateBack(): void {
    this.router.navigate(['/contracts']);
  }

  contractAllowsFreeze(): boolean {
    return statusAllowsContractFreeze(this.contract?.status);
  }

  contractAllowsUnfreeze(): boolean {
    return statusAllowsContractUnfreeze(this.contract?.status);
  }

  contractShowsHeaderActionsMenu(): boolean {
    return !!(this.contract && (this.contractAllowsFreeze() || this.contractAllowsUnfreeze()));
  }

  toggleContractHeaderMenu(event: Event): void {
    event.stopPropagation();
    this.showContractHeaderMenu = !this.showContractHeaderMenu;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClickHideHeaderMenu(event: MouseEvent): void {
    const t = event.target as HTMLElement;
    if (!t.closest('.contract-view-header-menu')) {
      this.showContractHeaderMenu = false;
    }
  }

  openFreezeModalFromHeader(): void {
    if (!this.contractAllowsFreeze()) {
      this.errorService.showErrorMessage(
        'Só é possível congelar contratos vigentes, próximos ao vencimento ou aguardando pagamento.'
      );
      return;
    }
    this.showContractHeaderMenu = false;
    this.showFreezeModal = true;
  }

  closeFreezeModal(): void {
    this.showFreezeModal = false;
  }

  onContractFrozen(res: ContractFreezeResponse): void {
    const msg =
      res?.message ||
      (res?.freezeStartUtc && res?.freezeEndUtc
        ? `Congelamento registrado (${res.status || 'Congelado'}).`
        : 'Contrato congelado com sucesso.');
    this.errorService.showInfoMessage(msg);
    this.closeFreezeModal();
    void this.loadContractComplete();
    this.loadContractDocuments();
  }

  confirmUnfreezeFromHeader(): void {
    if (!this.contractAllowsUnfreeze()) {
      this.errorService.showErrorMessage('Apenas contratos em estado congelado podem ser descongelados.');
      return;
    }
    this.showContractHeaderMenu = false;
    this.confirmationService
      .confirm({
        title: 'Descongelar contrato',
        message:
          'Deseja descongelar este contrato agora (antes do fim do período de congelamento)?',
        confirmText: 'Descongelar',
        cancelText: 'Cancelar',
        type: 'warning'
      })
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.isLoading = true;
        this.contractsService.unfreezeContract(this.contractId).subscribe({
          next: (res: ContractUnfreezeResponse) => {
            this.errorService.showInfoMessage(res.message || 'Contrato descongelado com sucesso.');
            this.isLoading = false;
            void this.loadContractComplete();
            this.loadContractDocuments();
          },
          error: () => {
            this.isLoading = false;
          }
        });
      });
  }

  navigateToChecklist(checklistId: number): void {
    if (checklistId) {
      this.router.navigate(['/checklist', checklistId], {
        queryParams: { fromContract: this.contractId }
      });
    }
  }

  createAndGoToChecklist(): void {
    if (!this.contractId) return;
    this.router.navigate(['/checklist/new'], {
      queryParams: { fromContract: this.contractId }
    });
  }

  navigateDepositRefund(): void {
    if (!this.contractId) return;
    this.router.navigate(['/contracts', this.contractId, 'deposit-refund']);
  }

  confirmDepositPayment(): void {
    if (!this.contractId) {
      this.errorService.showErrorMessage('ID do contrato não disponível');
      return;
    }

    this.isLoading = true;
    this.contractsService.confirmDepositPayment(this.contractId).subscribe({
      next: () => {
        this.errorService.showInfoMessage('💚 Pagamento de caução confirmado!');
        this.isLoading = false;
        this.loadContractComplete();
        this.loadContractDocuments();
      },
      error: () => { this.isLoading = false; }
    });
  }

  confirmFirstInstallmentPayment(): void {
    if (!this.contractId) {
      this.errorService.showErrorMessage('ID do contrato não disponível');
      return;
    }

    this.isLoading = true;
    this.contractsService.confirmFirstInstallmentPayment(this.contractId).subscribe({
      next: () => {
        this.errorService.showInfoMessage('💚 Pagamento da 1ª parcela confirmado!');
        this.isLoading = false;
        this.loadContractComplete();
        this.loadContractDocuments();
      },
      error: () => { this.isLoading = false; }
    });
  }

  getSignedDocumentsCount(): number {
    return this.signedDocuments.length;
  }

  getDocumentTypeLabel(documentType: string | undefined): string {
    if (!documentType) return '';
    return documentType.trim().toLowerCase() === 'contract' ? 'Contrato' : documentType;
  }

  getSignedVersionsList(): string {
    return this.signedDocuments.length === 0 
      ? '' 
      : this.signedDocuments.map(d => d.versaoFormatada).join(', ');
  }

  get lastSignedDocumentForView(): ContractDocument | null {
    return this.signedDocuments.length > 0 ? this.signedDocuments[0] : null;
  }

  get pendingDocumentToShow(): ContractDocument | null {
    if (!this.lastPendingDocument) return null;
    const lastSigned = this.lastSignedDocumentForView;
    if (!lastSigned) return this.lastPendingDocument;
    
    const pendingVersao = this.lastPendingDocument.versao ?? 0;
    const signedVersao = lastSigned.versao ?? 0;
    return pendingVersao > signedVersao ? this.lastPendingDocument : null;
  }
}